<template>
  <v-container
    id="integrations-view"
  >
    <material-card
      color="primary"
      icon="mdi-toolbox-outline"
    >
      <template #title>
        Integrations
      </template>

      <v-container fluid>
        <v-progress-linear v-if="loading" color="primary" indeterminate />

        <v-row v-else>
          <v-col
            v-for="integration in integrations"
            :key="integration.slug"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="d-flex flex-column fill-height" outlined>
              <v-card-title>
                <v-tooltip top>
                  <template #activator="{ attrs, on }">
                    <v-btn
                      :aria-label="`Configure ${integration.name}`"
                      :min-width="integration.image ? 80 : 42"
                      class="mr-3 pa-0"
                      height="42"
                      text
                      v-bind="attrs"
                      v-on="on"
                      @click="openIntegration(integration.slug)"
                    >
                      <v-img
                        v-if="integration.image"
                        :alt="`${integration.name} logo`"
                        :src="integration.image"
                        contain
                        height="42"
                        width="80"
                      />
                      <v-avatar v-else color="primary" size="42">
                        <v-icon color="white">
                          {{ default_icon }}
                        </v-icon>
                      </v-avatar>
                    </v-btn>
                  </template>
                  <span>Configure {{ integration.name }}</span>
                </v-tooltip>
                {{ integration.name }}
              </v-card-title>

              <v-card-text class="flex-grow-1">
                {{ integration.description }}
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="openIntegration(integration.slug)"
                >
                  <v-icon left>mdi-cog</v-icon>
                  Configure
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </material-card>

    <v-dialog v-model="serviceTitanDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>
          Configure ServiceTitan
        </v-card-title>
        <v-divider />

        <v-card-text>
          <v-form>
            <v-container fluid>
              <v-row dense>
                <template>
                  <v-col cols="12" md="8">
                    <v-card class="fill-height" outlined>
                      <v-card-title class="subtitle-1">
                        Configuration
                      </v-card-title>
                      <v-divider />
                      <v-card-text>
                        <v-row dense>
                          <v-col cols="12">
                            <v-card outlined>
                              <v-card-title class="subtitle-1">
                                App Data
                              </v-card-title>
                              <v-divider />
                              <v-card-text>
                                <v-row dense>
                                  <v-col cols="12">
                                    <v-text-field
                                      v-model="settings.service_titan.id"
                                      label="Tenant ID"
                                    ></v-text-field>
                                  </v-col>
                                  <template v-if="settings.service_titan.id && settings.service_titan.integration">
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.client_id"
                                        label="Client ID"
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.client_secret"
                                        :append-icon="showClientSecret ? 'mdi-eye' : 'mdi-eye-off'"
                                        :type="showClientSecret ? 'text' : 'password'"
                                        label="Client Secret"
                                        @click:append="showClientSecret = !showClientSecret"
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.vendor_id"
                                        hint="The vendor ID used for creating purchase orders or managing pricebook."
                                        label="Vendor ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col v-if="settings.service_titan.app_version === 2" cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.pending_approval_delay"
                                        hint="The delay in processing pending orders."
                                        label="Pending Approval Delay (hours)"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12">
                                      <v-text-field
                                        v-model="settings.service_titan.notification_email"
                                        hint="The email address of who should receive notification alerts."
                                        label="Notification Alerts Email"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col class="d-flex" cols="12">
                                      <v-switch
                                        v-model="settings.service_titan.create_po"
                                        class="mr-8"
                                        label="Display Purchase Order Creation"
                                      ></v-switch>
                                      <v-switch
                                        v-if="settings.service_titan.app_version === 2"
                                        v-model="settings.service_titan.manage_pricebook"
                                        label="Display Pricebook Management"
                                      ></v-switch>
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </v-col>
                          <template v-if="settings.service_titan.id && settings.service_titan.integration">
                            <v-col v-if="settings.service_titan.create_po" cols="12">
                              <v-card outlined>
                                <v-card-title class="subtitle-1">
                                  Purchase Order Creation
                                </v-card-title>
                                <v-divider />
                                <v-card-text>
                                  <v-row dense>
                                    <v-col v-if="settings.service_titan.app_version === 2" cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.inventory_location_id"
                                        hint="The inventory location ID used when creating purchase orders."
                                        label="Inventory Location ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.order_type_id"
                                        hint="ID of the purchase order type used when creating purchase orders."
                                        label="Purchase Order Type ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.sku_id"
                                        hint="The sku ID from pricebook, can be any active material or equipment. Used when creating purchase orders."
                                        label="SKU ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.technician_id"
                                        hint="Default technician id for purchase orders. Only used if no technician found on the job."
                                        label="Default Technician ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </v-card>
                            </v-col>
                            <v-col v-if="settings.service_titan.app_version === 2 && settings.service_titan.manage_pricebook" cols="12">
                              <v-card outlined>
                                <v-card-title class="subtitle-1">
                                  Pricebook Management
                                </v-card-title>
                                <v-divider />
                                <v-card-text>
                                  <v-row dense>
                                    <v-col v-if="settings.service_titan.app_version === 2" cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.service_category_id"
                                        hint="The service category ID used for pricebook management."
                                        label="Service Category ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                    <v-col v-if="settings.service_titan.app_version === 2" cols="12" md="6">
                                      <v-text-field
                                        v-model="settings.service_titan.pricebook_category_id"
                                        hint="The pricebook category ID. Required if we are uploading your pricebook items."
                                        label="Pricebook Category ID"
                                        persistent-hint
                                      ></v-text-field>
                                    </v-col>
                                  </v-row>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </template>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-title class="subtitle-1">
                        <span>Setup Instructions</span>
                        <v-spacer />
                        <v-btn
                          :aria-expanded="showSetupInstructions"
                          aria-label="Toggle setup instructions"
                          icon
                          @click="showSetupInstructions = !showSetupInstructions"
                        >
                          <v-icon>
                            {{ showSetupInstructions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          </v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-expand-transition>
                        <div v-show="showSetupInstructions">
                          <v-divider />
                          <v-card-text>
                            <ol class="pl-4">
                              <li class="mb-3">
                                Within your ServiceTitan account, go to <strong>Settings</strong>.
                              </li>
                              <li class="mb-3">
                                Under the <strong>Integrations</strong> tab, select <strong>API Application Access</strong>.
                              </li>
                              <li v-if="!settings.service_titan.integration" class="mb-3">
                                After adding your <strong>Tenant ID</strong>, you will receive an email within 24 hours informing you that you may complete the setup.
                              </li>
                              <template v-if="settings.service_titan.integration">
                                <li class="mb-3">
                                  Click <strong>Connect New App</strong>.
                                </li>
                                <li class="mb-3">
                                  Select the <strong>ecDirect - V2</strong> app, then click <strong>Connect</strong>.
                                </li>
                                <li class="mb-3">
                                  Click <strong>Allow Access</strong> in the popup window.
                                </li>
                                <li class="mb-3">
                                  After the app is connected, you will be directed to the <strong>Application Details</strong> page.
                                </li>
                                <li class="mb-3">
                                  The first two boxes show your <strong>Client ID</strong> and <strong>Client Secret</strong>. We need both to complete the integration.
                                </li>
                                <li class="mb-3">
                                  Select <strong>Copy</strong> next to the Client ID and paste it into this form.
                                </li>
                                <li>
                                  Click <strong>Generate</strong> to show your unique Client Secret. <strong>The Client Secret is visible only once</strong>, so copy it and paste it into this form.
                                </li>
                              </template>
                            </ol>
                          </v-card-text>
                        </div>
                      </v-expand-transition>
                    </v-card>
                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            :disabled="!settings.service_titan.active"
            color="error"
            text
            @click="deactivateServiceTitan"
          >
            DEACTIVATE
          </v-btn>
          <v-spacer />
          <v-btn text @click="serviceTitanDialog = false">
            CANCEL
          </v-btn>
          <v-btn color="primary" @click="updateSettings">
            UPDATE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirmation-modal ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import _ from 'lodash'

export default {
  name: 'Integrations',
  components: {
    MaterialCard: () => import('@/components/MaterialCard'),
    ConfirmationModal: () => import('@/components/ConfirmationModal')
  },
  data () {
    return {
      settings: {
        service_titan: {}
      },
      default_icon: 'mdi-tools',
      loading: true,
      showClientSecret: false,
      showSetupInstructions: true,
      serviceTitanDialog: false
    }
  },
  computed: {
    ...mapState({
      integrations: state => state.integrations
    }),
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapState('contractorManagement', {
      contractor: state => state.contractor
    })
  },
  methods: {
    ...mapActions({
      fetchIntegrations: 'fetchIntegrations'
    }),
    ...mapActions('contractorManagement', {
      fetch: 'fetchContractor',
      update: 'updateSettings'
    }),
    copySettings (settings = {}) {
      const copy = _.cloneDeep(settings)
      const serviceTitanDefaults = {
        id: 0,
        integration: false,
        active: false,
        sku_id: 0,
        vendor_id: 0,
        notification_email: '',
        client_id: '',
        client_secret: '',
        create_po: false,
        manage_pricebook: false,
        inventory_location_id: '',
        pricebook_category_id: '',
        service_category_id: '',
        pending_approval_delay: 0,
        app_version: 2,
        lead_type: '',
        order_type_id: 0,
        technician_id: 0,
        campaign_id: 0
      }

      copy.service_titan = Object.assign(serviceTitanDefaults, copy.service_titan || {})
      copy.service_titan.app_version = copy.service_titan.app_version || 2

      return copy
    },
    initSettings () {
      this.settings = this.copySettings(this.contractor.settings)
      this.loading = false
    },
    loadIntegrations () {
      this.fetchIntegrations()
        .then(() => {})
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
        })
    },
    loadContractor () {
      this.loading = true

      this.fetch(this.user.model_id)
        .then(() => {
          this.initSettings()
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
        })
    },
    openIntegration (integration) {
      if (integration === 'service_titan') {
        this.settings = this.copySettings(this.contractor.settings)
        this.showClientSecret = false
        this.serviceTitanDialog = true
      }
    },
    async deactivateServiceTitan () {
      const wasActive = this.settings.service_titan.active
      this.settings.service_titan.active = false
      const updated = await this.updateSettings()

      if (!updated) {
        this.settings.service_titan.active = wasActive
      }
    },
    async updateSettings () {
      if (await this.$refs.confirm.open('Update Integrations', 'Are you sure you want to update your integrations?', { color: 'red', zIndex: 300 })) {
        const base = _.cloneDeep(this.contractor.settings)
        const diff = this.difference(this.settings, base)

        try {
          await this.update({ id: this.user.model_id, settings: diff })
          this.$toast.success('Integrations updated successfully!')
          this.serviceTitanDialog = false
          return true
        } catch (error) {
          this.$toast.error(error.data ? error.data.error : error)
        }
      }

      return false
    },
    difference (object, base) {
      function changes (object, base) {
        return _.transform(object, function (result, value, key) {
          if (!_.isEqual(value, base[key])) {
            result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value
          }
        })
      }
      return changes(object, base)
    }
  },
  created () {
    this.loadIntegrations()
    this.loadContractor()
  }
}
</script>

<style/>
